/**
 * DriveAccountChooser — "Import from which Drive?" (ED-2492).
 *
 * Mounted once at the app root. Registers itself with cloudPickers so the web
 * Picker can ask which connected Google account to import from when the user has
 * more than one connected. Avatars render when present (google_picture);
 * otherwise a Drive-glyph fallback. Inert on native (the OS picker handles
 * multi-account there, so the chooser is never invoked).
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Modal, Pressable, Text, TouchableOpacity, View, Image, StyleSheet } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import Icon from './Icon';
import { registerDriveAccountChooser, type DriveAccountOption } from '../utilities/cloudPickers';

export default function DriveAccountChooser() {
  const theme = useTheme();
  const [accounts, setAccounts] = useState<DriveAccountOption[] | null>(null);
  const resolverRef = useRef<((id: number | null) => void) | null>(null);

  useEffect(() => {
    registerDriveAccountChooser(
      (accs: DriveAccountOption[]) =>
        new Promise<number | null>((resolve) => {
          resolverRef.current = resolve;
          setAccounts(accs);
        }),
    );
    return () => registerDriveAccountChooser(null);
  }, []);

  const finish = useCallback((id: number | null) => {
    setAccounts(null);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(id);
  }, []);

  const styles = makeStyles(theme);

  return (
    <Modal visible={!!accounts} transparent animationType="fade" onRequestClose={() => finish(null)} statusBarTranslucent>
      <Pressable style={styles.overlay} onPress={() => finish(null)}>
        <Pressable style={styles.sheet} onPress={() => {}}>
          <Text style={styles.title}>Import from which Drive?</Text>
          <View style={styles.card}>
            {(accounts || []).map((a, i) => (
              <View key={a.connection_id}>
                {i > 0 && <View style={styles.divider} />}
                <TouchableOpacity style={styles.row} onPress={() => finish(a.connection_id)} activeOpacity={0.7}>
                  {a.google_picture ? (
                    <Image source={{ uri: a.google_picture }} style={styles.avatar} />
                  ) : (
                    <View style={[styles.avatar, styles.avatarFallback]}>
                      <Icon icon={['fab', 'google-drive']} size={16} color="#4285F4" />
                    </View>
                  )}
                  <Text style={styles.email} numberOfLines={1}>{a.google_email || 'Google Drive'}</Text>
                  <Icon icon={['fas', 'chevron-right']} size={12} color={theme.colors.muted} />
                </TouchableOpacity>
              </View>
            ))}
          </View>
          <TouchableOpacity style={styles.cancelRow} onPress={() => finish(null)}>
            <Text style={styles.cancelText}>Cancel</Text>
          </TouchableOpacity>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const makeStyles = (theme: ReturnType<typeof import('../hooks/useTheme').useTheme>) =>
  StyleSheet.create({
    overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', padding: 24 },
    sheet: { alignSelf: 'center', width: '100%', maxWidth: 420, borderRadius: 16, padding: 16, backgroundColor: theme.colors.card },
    title: { fontSize: 16, fontWeight: '700', color: theme.colors.text, marginBottom: 12, textAlign: 'center' },
    card: { borderRadius: 12, borderWidth: StyleSheet.hairlineWidth, overflow: 'hidden', borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
    row: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12, gap: 12 },
    avatar: { width: 32, height: 32, borderRadius: 16 },
    avatarFallback: { alignItems: 'center', justifyContent: 'center', backgroundColor: '#4285F418' },
    email: { flex: 1, fontSize: 15, fontWeight: '500', color: theme.colors.text },
    divider: { height: StyleSheet.hairlineWidth, marginLeft: 14, backgroundColor: theme.colors.border },
    cancelRow: { marginTop: 12, paddingVertical: 12, alignItems: 'center' },
    cancelText: { fontSize: 16, fontWeight: '600', color: theme.colors.error },
  });
